import React, { useState, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f1c40f, #f39c12);
  padding: 2rem;
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const Timer = styled.div`
  font-size: 2rem;
  text-align: center;
  margin: 1rem 0;
  color: ${props => props.isWarning ? '#e74c3c' : '#2c3e50'};
`;

const PromptSection = styled.div`
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 12px;
  margin: 2rem 0;
`;

const RecordingControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
`;

const Button = styled.button`
  background: ${props => props.isRecording ? '#e74c3c' : '#f1c40f'};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }
`;

const AudioVisualizer = styled.div`
  height: 100px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SpeakingTest = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState(45); // 45 seconds for speaking
  const [prepTime, setPrepTime] = useState(30); // 30 seconds prep time
  const [isPreparing, setIsPreparing] = useState(true);
  const timerRef = useRef(null);

  React.useEffect(() => {
    if (isPreparing && prepTime > 0) {
      timerRef.current = setInterval(() => {
        setPrepTime(prev => prev - 1);
      }, 1000);
    } else if (!isPreparing && isRecording && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    }

    return () => clearInterval(timerRef.current);
  }, [isPreparing, isRecording, prepTime, timeLeft]);

  const handleStartRecording = () => {
    setIsRecording(true);
    setIsPreparing(false);
  };

  const handleStopRecording = () => {
    setIsRecording(false);
  };

  return (
    <Container>
      <Content>
        <h1>Speaking Test - Task 1</h1>

        {isPreparing ? (
          <>
            <Timer isWarning={prepTime < 10}>
              Preparation Time: {prepTime}s
            </Timer>
            <PromptSection>
              <h2>Question:</h2>
              <p>
                Describe a memorable experience from your academic life and explain 
                why it was significant to you.
              </p>
            </PromptSection>
          </>
        ) : (
          <>
            <Timer isWarning={timeLeft < 10}>
              Recording Time: {timeLeft}s
            </Timer>
            <AudioVisualizer>
              {isRecording ? "Recording in progress..." : "Ready to record"}
            </AudioVisualizer>
          </>
        )}

        <RecordingControls>
          {isPreparing ? (
            <Button onClick={() => setIsPreparing(false)}>
              I'm Ready to Speak
            </Button>
          ) : (
            <Button
              isRecording={isRecording}
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              disabled={timeLeft === 0}
            >
              {isRecording ? "Stop Recording" : "Start Recording"}
            </Button>
          )}
        </RecordingControls>
      </Content>
    </Container>
  );
};

export default SpeakingTest; 